/* import __COLOCATED_TEMPLATE__ from './team-multiselect.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { cached } from 'tracked-toolbox';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type TeamSummary from 'embercom/objects/inbox/team-summary';

interface Arguments {
  selectedTeams?: number[];
  onTeamsChange?: (selectedTeams: number[]) => void;
}

interface Signature {
  Args: Arguments;
  Element: HTMLDivElement;
}

export default class TeamMultiselect extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked selectedTeams: number[] = [];
  @tracked teamCache = new Map<number, TeamSummary>();

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    this.selectedTeams = this.args.selectedTeams || [];
  }

  willDestroy(): void {
    super.willDestroy();
    this.teamCache.clear();
  }

  get hasSelectedTeams(): boolean {
    return this.selectedTeams.length > 0;
  }

  @cached
  get selectedTeamObjects(): TeamSummary[] {
    return this.selectedTeams
      .map((teamId) => this.teamCache.get(teamId))
      .filter((team): team is TeamSummary => Boolean(team));
  }

  @action onSelectTeam(teamSummary: TeamSummary): void {
    this.teamCache.set(teamSummary.id, teamSummary);

    if (this.selectedTeams.includes(teamSummary.id)) {
      this.selectedTeams = this.selectedTeams.filter((id) => id !== teamSummary.id);
    } else {
      this.selectedTeams = [...this.selectedTeams, teamSummary.id];
    }

    this.args.onTeamsChange?.(this.selectedTeams);
  }

  @action removeTeam(teamId: number): void {
    this.selectedTeams = this.selectedTeams.filter((id) => id !== teamId);

    this.args.onTeamsChange?.(this.selectedTeams);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Teammates::Invites::TeamMultiselect': typeof TeamMultiselect;
    'settings/teammates/invites/team-multiselect': typeof TeamMultiselect;
  }
}
